// App.tsx
import React from 'react';
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom';

import FrontInstructionsPage from './pages/FrontInstructionsPage';
import SideInstructionsPage from './pages/SideInstructionsPage';
import ResultPage from './pages/ResultPage';
import PhotoCapture from './pages/PhotoCapture';
import ClientDashboard from './pages/ClientDashboard';
import ClientSelection from './pages/ClientList';
import CoachOverview from './pages/CoachOverview';
import CreateClient from './pages/CreateClient';
import CamTest from './pages/WebcamTest';
import PoseDebugPage from './pages/PoseDebugPage';
import ScanV2Page from './pages/ScanV2Page';
import SuperAdminPage from './pages/SuperAdminPage';
import MyContextProvider from './MyContext';
import { Toaster } from './design-system/primitives/toaster';
import { ImpersonationProvider } from './context/ImpersonationContext';
import ImpersonationBanner from './components/common/ImpersonationBanner';
import Gdpr from './components/common/Gdpr';
import { DataModeBanner } from './design-system/shell/data-mode-banner';
import { dataMode, isDemoBuild, setDataMode } from './lib/demo/mode';
import { currentConfig } from './lib/domainConfig';
import './theme/tailwind.css';

/**
 * Capture and its instruction screens.
 *
 * These used to be the one subtree still inside <IonApp>, kept because
 * ion-page and ion-content carried Android behaviour — safe-area insets, the
 * hardware back button, keyboard avoidance. The app is browser-only, so none
 * of that was ever doing anything here: what remained was a full-height box
 * with an internal scroll container, which CaptureScreen is.
 *
 * Keyed on the path so React unmounts and remounts on every navigation. That
 * is what IonRouterOutlet's key was doing and it is load-bearing: the capture
 * flow holds camera and MediaPipe state that must not survive a move between
 * views.
 */
const CaptureRoutes: React.FC = () => {
  const location = useLocation();
  return (
    <>
      {/* Consent, asked once before any photograph is taken.
          Gdpr opens itself when it has never been answered, so mounting it
          here is the whole wiring. It had none: the component existed, the
          only thing that rendered it passed a prop that was never set, and
          then that thing was deleted with the Ionic toolbar. Nobody has been
          asked for a while. */}
      <Gdpr isOpen={false} />
      <Switch key={location.pathname}>
      <Route exact path="/cam" component={CamTest} />
      <Route exact path="/cam/:pose" component={CamTest} />
      <Route exact path="/pose-debug" component={PoseDebugPage} />
      <Route exact path="/scan/:profileID/:sessionID" component={ScanV2Page} />
      <Route
        exact
        path="/sessions/:profileID/:sessionID/:gender/front_img"
        component={FrontInstructionsPage}
      />
      <Route
        exact
        path="/sessions/:profileID/:sessionID/:gender/right_img"
        component={SideInstructionsPage}
      />
      <Route
        exact
        path="/sessions/:profileID/:sessionID/:gender/result"
        component={ResultPage}
      />
      <Route
        exact
        path="/photo/:profileID/:sessionID/:gender/:pose"
        component={PhotoCapture}
      />
      </Switch>
    </>
  );
};

/**
 * Enumerated rather than left as the catch-all. A bare `<Route
 * component={CaptureRoutes}/>` swallowed every unknown path into an IonApp
 * whose outlet matched nothing, so a typo rendered a blank page instead of
 * bouncing to the roster.
 */
const CAPTURE_PATHS = [
  '/cam',
  '/cam/:pose',
  '/pose-debug',
  '/scan/:profileID/:sessionID',
  '/sessions/:profileID/:sessionID/:gender/front_img',
  '/sessions/:profileID/:sessionID/:gender/right_img',
  '/sessions/:profileID/:sessionID/:gender/result',
  '/photo/:profileID/:sessionID/:gender/:pose',
];

// index.html says "Sizewall" for every host; the product's own name replaces
// it once the config is known ("Sizewall Retail" on a retail host).
if (typeof document !== 'undefined') document.title = currentConfig.strings.title;

const App: React.FC = () => (
  <ImpersonationProvider>
    <MyContextProvider>
      <BrowserRouter>
        {/* A column so the banners keep their space and ion-app takes the
            rest. Without it ion-app's absolute positioning covers them, and
            the capture flow — where a coach most needs to know whether this
            is real data — showed no banner at all. */}
        <div className="flex min-h-[100dvh] flex-col">
        {/* Only a demo build can be either. A production build is always live
            and says nothing, because there is nothing to disambiguate. */}
        {isDemoBuild && <DataModeBanner mode={dataMode()} onSwitch={setDataMode} />}
        <ImpersonationBanner />
        <Switch>
          {/* The coach desktop was a clean-room proof that the concept could
              be rendered outside IonApp. The whole shell is that now, and the
              dashboard's overview section is the same surface with the rail
              around it — so this is one page, reached by two URLs, rather than
              two pages drifting apart. Redirected rather than removed: the
              route was never linked from the app, but it may be bookmarked. */}
          <Redirect exact from="/coach/:id" to="/dashboard/:id" />
          <Route exact path="/dashboard/:id" component={ClientDashboard} />
          <Route exact path="/overview" component={CoachOverview} />
          <Route exact path="/clients" component={ClientSelection} />
          <Route exact path="/create-client" component={CreateClient} />
          <Route exact path="/superadmin" component={SuperAdminPage} />
          <Route exact path={CAPTURE_PATHS} component={CaptureRoutes} />
          {/* Both the root and any unknown path. Overview, not the client
              list: the deck's principle slide is that a coach's first ten
              seconds should answer "who needs me now", which a roster does
              not. */}
          <Redirect to="/overview" />
        </Switch>
        </div>
        <Toaster />
      </BrowserRouter>
    </MyContextProvider>
  </ImpersonationProvider>
);

export default App;
